import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFirestore } from '@angular/fire/firestore';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Ativacao, User } from '../models/models';
import { environment } from './../../environments/environment';
import { AtivacoesService } from './ativacoes.service';
import { ApiServiceService } from './requests/api/api-service.service';
import { GenericFirebaseRequestsService } from './requests/firebase/generic-firebase-requests.service';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class SecurityService {
  public currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;
  public logado = false;
  public isLogin = false;

  constructor(
    private fireAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private ativacoesService: AtivacoesService,
    private router: Router,
    private genericFireReqs: GenericFirebaseRequestsService,
    private utils: UtilsService,
    private api: ApiServiceService,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog
  ) {
    const userInMemory = JSON.parse(
      localStorage.getItem('currentUser') as string
    );

    if (userInMemory?.resumoAtivacoes) {
      delete userInMemory?.resumoAtivacoes;
    }

    this.currentUserSubject = new BehaviorSubject<User>(userInMemory);
    this.currentUser = this.currentUserSubject.asObservable();

    this.fireAuth.user.subscribe(async (res) => {
      if (res === null) {
        this.logout().then((res) => {
          this.router.navigate(['/login']);
        });
      }
      if (res?.email) {
        this.logado = true;

        const userSimulado = window.localStorage.getItem('user-simulado');
        const userEmail =
          userSimulado && userSimulado?.length > 0 && this.logado
            ? userSimulado
            : res.email;

        await this.updateUserRefs2(userEmail, null, null, true, this.isLogin);
        this.currentUserSubject.next(
          JSON.parse(
            localStorage.getItem('currentUser') as string
          ) as unknown as User
        );
        this.router.navigate(['']);
      }
    });

    if (this.currentUserValue == null) {
      this.fireAuth.signOut();
    }
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  get canChooseCampaign() {
    if ('escolha_campanha' in this.currentUserValue) {
      return this.currentUserValue.escolha_campanha;
    }

    if (
      'escolha_campanha' in
      (this.currentUserValue as any)?.empresa?.config_crm?.appvnd
    ) {
      return (this.currentUserValue as any)?.empresa?.config_crm.appvnd
        .escolha_campanha;
    }

    return false;
  }

  logout() {
    localStorage.removeItem('currentUser');
    return this.fireAuth.signOut();
  }

  resetarSenha(email) {
    if (email == null) {
      email = this.currentUserValue.email;
    }
    return this.fireAuth.sendPasswordResetEmail(email);
  }

  makeLogin(email, password) {
    this.isLogin = true;

    return new Promise((resolve, reject) => {
      this.fireAuth
        .signInWithEmailAndPassword(email, password)
        .then(async (cred) => {
          resolve(null);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getAppInfo() {
    const connection = (navigator as any)?.connection || {};

    return {
      url: window.location.origin + this.router.url,
      version: environment.appVersion,
      type: this.getAppType(),
      screenResolution: this.getScreenResolution(),
      language: navigator.language,
      userAgent: navigator.userAgent,
      connection: {
        networkType: connection?.type || 'unknown',
        networkQuality: connection?.effectiveType || 'unknown',
        networkDownlinkMax: connection?.downlinkMax || 'unknown',
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  private getAppType(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('android')) return 'android';
    if (userAgent.includes('iphone') || userAgent.includes('ipad'))
      return 'ios';
    return 'web';
  }

  private getScreenResolution(): string {
    return `${window.screen.width}x${window.screen.height}`;
  }

  updateUserRefs2(
    email: string,
    cpf: string | null = null,
    tel: string | null = null,
    campanhaMode: boolean = true,
    logging: boolean = false
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (this.isLogin) {
        this.isLogin = false;
      }

      if (email) {
        this.api
          .sendPostRequest(environment.cloudApi.endpoints.initialData, {
            initial_data: {
              email,
              client: {
                campanha: this.ativacoesService.campanhaNameToFilter
                  ? this.ativacoesService.campanhaNameToFilter
                  : null,
                cpf,
                tel,
              },
            },
          })
          .subscribe((initialData: any) => {
            console.log(initialData);

            if (!initialData) {
              if (logging) {
                this.logout();
              } else {
                this.utils.popSnackBar(
                  'Erro ao realizar ação. Tente novamente.', 5, true
                );
              }

              reject(new Error('Erro ao realizar request.'));
            }

            const user = initialData.user.app_martech_vendedores;
            user.empresa = initialData.company;
            user.has_lembretes = initialData.user.has_lembretes;
            this.ativacoesService.user = user;
            this.currentUserSubject.next(user);

            if (campanhaMode) {
              this.ativacoesService.clienteAtivacaoAtual = initialData
                .next_client.app_martech_info_cliente
                ? initialData.next_client.app_martech_info_cliente
                : null;
              this.ativacoesService.ativacaoAtual = initialData.next_client
                .ativacao
                ? initialData.next_client.ativacao
                : null;
            }

            if (user) {
              localStorage.setItem('currentUser', JSON.stringify(user));
              resolve(initialData);
            } else {
              if (logging) {
                this.logout();
              } else {
                this.utils.popSnackBar(
                  'Erro ao realizar ação. Tente novamente.', 5, true
                );
              }

              reject(new Error('Erro ao realizar request.'));
            }
          });
      } else {
        await this.fireAuth.signOut();
      }
    });
  }

  finishContact(
    campanhaMode: boolean = true,
    actionType: string,
    ativacao: Ativacao
  ): Promise<any> {
    if (campanhaMode) {
      this.ativacoesService.ativacaoAtual = null;
      this.ativacoesService.clienteAtivacaoAtual = null;
    }

    return new Promise(async (resolve, reject) => {
      this.api
        .sendPostRequest(environment.cloudApi.endpoints.finishContact, {
          initial_data: {
            email: this.currentUserValue.email,
            client: {
              campanha: this.ativacoesService.campanhaNameToFilter
                ? this.ativacoesService.campanhaNameToFilter
                : null,
            },
          },
          finish_contact: {
            action_type: actionType,
            sigla_marca: this.currentUserValue.sigla_marca,
            id_ativacao: ativacao.id_ativacao,
            events: this.ativacoesService.eventsOnQueue,
          },
          app_info: this.getAppInfo(),
        })
        .subscribe((initialData: any) => {
          console.log(initialData);

          if (!initialData) {
            this.utils.popSnackBar('Erro ao realizar ação. Tente novamente.', 5, true);
            reject(new Error('Erro ao realizar request.'));
          }

          this.ativacoesService.eventsOnQueue = [];

          const user = initialData.user.app_martech_vendedores as User;
          user.empresa = initialData.company;
          user.has_lembretes = initialData.user.has_lembretes;
          this.ativacoesService.user = user;
          this.currentUserSubject.next(user);

          if (
            campanhaMode &&
            (!initialData.next_client.app_martech_info_cliente ||
              user?.status_bloqueio?.bloqueado)
          ) {
            this.router.navigate(['/']);
            resolve(initialData);
          }

          if (campanhaMode) {
            this.ativacoesService.clienteAtivacaoAtual = initialData.next_client
              .app_martech_info_cliente
              ? initialData.next_client.app_martech_info_cliente
              : null;
            this.ativacoesService.ativacaoAtual = initialData.next_client
              .ativacao
              ? initialData.next_client.ativacao
              : null;
          }

          if (user) {
            localStorage.setItem('currentUser', JSON.stringify(user));
            resolve(initialData);
          } else {
            this.utils.popSnackBar('Erro ao realizar ação. Tente novamente.', 5, true);
            reject(new Error('Erro ao realizar request.'));
          }
        });
    });
  }

  mainActivation(
    ativacao: Ativacao,
    tipoMidia: string | null,
    goToNext = true
  ) {
    this.spinner.show();

    this.registrarEvento('finalizou-ativacao', {
      id_cliente: ativacao.id_cliente,
      id_ativacao: ativacao.id_ativacao,
      id_vendedor: ativacao.id_vendedor,
    });

    const executouEvent = {
      id_cliente: ativacao.id_cliente,
      id_ativacao: ativacao.id_ativacao,
      id_vendedor: ativacao.id_vendedor,
    };

    if (tipoMidia) {
      (executouEvent as any).midia = tipoMidia;
    }

    this.registrarEvento('executou-ativacao', {
      midia: tipoMidia,
      id_cliente: ativacao.id_cliente,
      id_ativacao: ativacao.id_ativacao,
      id_vendedor: ativacao.id_vendedor,
    });

    return this.finishContact(goToNext, 'padrao', ativacao).then((res) => {
      if (goToNext) {
        document.getElementById('content')?.scrollTo(0, 0);
      }

      this.spinner.hide();
    });
  }

  finishWithNoContact(ativacao: Ativacao, motivo: string) {
    this.spinner.show();

    this.registrarEvento('finalizou-ativacao', {
      id_cliente: ativacao.id_cliente,
      id_ativacao: ativacao.id_ativacao,
      id_vendedor: ativacao.id_vendedor,
    });

    this.registrarEvento('finalizou-sem-ativacao', {
      id_cliente: ativacao.id_cliente,
      id_ativacao: ativacao.id_ativacao,
      id_vendedor: ativacao.id_vendedor,
      motivo,
    });

    return this.finishContact(true, 'padrao', ativacao).then((res) => {
      document.getElementById('content')?.scrollTo(0, 0);

      this.spinner.hide();
    });
  }

  yes(ativacao: Ativacao, tipoMidia: string) {
    this.mainActivation(ativacao, tipoMidia, false).then((res) => {
      this.dialog.closeAll();
      ativacao.executou_acao = true;
    });
  }

  enviarFinalFila(ativacao: Ativacao) {
    this.spinner.show();

    this.registrarEvento('enviou-final-fila', {
      id_cliente: ativacao.id_cliente,
      id_ativacao: ativacao.id_ativacao,
      id_vendedor: ativacao.id_vendedor,
    });

    return this.finishContact(true, 'final-da-fila', ativacao).then((res) => {
      document.getElementById('content')?.scrollTo(0, 0);
      this.spinner.hide();
    });
  }

  descadastrar(
    ativacao: Ativacao,
    actionType = 'padrao',
    eventName = 'descadastro'
  ) {
    this.spinner.show();

    if (eventName == 'descadastro') {
      this.registrarEvento('finalizou-ativacao', {
        id_cliente: ativacao.id_cliente,
        id_ativacao: ativacao.id_ativacao,
        id_vendedor: ativacao.id_vendedor,
      });
    }

    this.registrarEvento(eventName, ativacao);

    this.finishContact(true, actionType, ativacao).then((res) => {
      document.getElementById('content')?.scrollTo(0, 0);
      this.spinner.hide();
    });
  }

  registrarEvento(nome_evento: string, objeto: any) {
    const event = {
      objeto,
      nome_evento: nome_evento,
      origem: 'appvnd',
      data_evento: this.currentDateInSaoPaulo,
      sigla_marca: environment.homolog
        ? 'MYN'
        : this.currentUserValue.sigla_marca,
    };

    this.ativacoesService.eventsOnQueue.push(event);
    console.log(this.ativacoesService.eventsOnQueue);
    return of({});
  }

  get currentDateInSaoPaulo(): string {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'America/Sao_Paulo',
    };

    // Use Intl.DateTimeFormat and manually join parts for the correct format
    const formatter = new Intl.DateTimeFormat('en-GB', options);
    const parts = formatter.formatToParts(now);

    const day = parts.find((p) => p.type === 'day')?.value;
    const month = parts.find((p) => p.type === 'month')?.value;
    const year = parts.find((p) => p.type === 'year')?.value;
    const hour = parts.find((p) => p.type === 'hour')?.value;
    const minute = parts.find((p) => p.type === 'minute')?.value;
    const second = parts.find((p) => p.type === 'second')?.value;

    // Return the formatted date string without commas
    return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
  }

  async setCampaign(campaignToFilter: string | null) {
    this.ativacoesService.ativacaoAtual = null;
    this.ativacoesService.clienteAtivacaoAtual = null;
    this.ativacoesService.campanhaNameToFilter = campaignToFilter;
    await this.updateUserRefs2(this.currentUserValue.email);
  }

  getUserData(userEmail: string): Observable<User> {
    return this.genericFireReqs.getDocument<User>(
      environment.DB_vendedores,
      userEmail,
      true
    );
  }

  isUserAdmin(): Observable<boolean> {
    return this.fireAuth.user.pipe(
      switchMap((user) => {
        return this.getUserData(user.email).pipe(
          map((userData) => !!userData.admin)
        );
      })
    );
  }

  get userSimulado(): string | undefined {
    return window.localStorage.getItem('user-simulado');
  }

  setUserSimulado(userEmail) {
    this.getUserData(userEmail).subscribe((res) => {
      if (!res) {
        this.utils.popSnackBar('Usuário não encontrado.', 5, true);
      } else {
        window.localStorage.setItem('user-simulado', userEmail);
        window.location.reload();
      }
    });
  }

  resetUserSimulado() {
    window.localStorage.removeItem('user-simulado');
    window.location.reload();
  }

  /*
  async checkUpdate() {
    const stableVersion = localStorage.getItem('stable-version');
    if (!stableVersion) {
      await this.fireAuth.signOut();
      localStorage.setItem('stable-version', '1.2.8');
      location.reload();
    }
  }
  */
}
